<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate24h } from '@/utils/dateFormatter'
import { useToast } from '@/composables/useToast'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'Settings',
  },
})

const { t } = useI18n()
const { showToast } = useToast()

// 👉 Data
const isDialogVisible = ref(false)
const isEditDialogVisible = ref(false)
const currentApp = ref<any>(null)
const saving = ref(false)

// 👉 Table data (removed pagination for VDataTable)

// 👉 Table headers
const headers = computed(() => [
  {
    title: t('common.name'),
    key: 'name',
    sortable: true,
  },
  {
    title: t('common.description'),
    key: 'description',
    sortable: true,
  },
  {
    title: t('common.status'),
    key: 'isActive',
    sortable: true,
  },
  {
    title: t('common.dimensions'),
    key: 'dimensions',
    sortable: true,
  },
  {
    title: t('common.createdAt'),
    key: 'createdAt',
    sortable: true,
  },
  {
    title: t('common.actions'),
    key: 'actions',
    sortable: false,
  },
])

// 👉 Form data
const appForm = ref({
  name: '',
  description: '',
  url: '',
  icon: '',
  isActive: true,
  width: 1200,
  height: 800,
  allowFullscreen: true,
})

// 👉 Search and filter options
const searchQuery = ref('')
const selectedStatus = ref()
const selectedRole = ref()

// Status options
const status = computed(() => [
  { title: t('common.active'), value: true },
  { title: t('common.inactive'), value: false },
])

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Update data table options
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// 👉 Fetching external apps
const { data: externalAppsData, execute: loadExternalApps, isFetching: loading } = useApi<any>(createUrl('/external-apps', {
  query: {
    q: searchQuery,
    status: selectedStatus,
    itemsPerPage,
    page,
    sortBy,
    orderBy,
  },
}))

const externalApps = computed(() => externalAppsData.value?.data || [])
const totalItems = computed(() => externalAppsData.value?.total || 0)

// 👉 Open add dialog
const openAddDialog = () => {
  currentApp.value = null
  appForm.value = {
    name: '',
    description: '',
    url: '',
    icon: '',
    isActive: true,
    width: 1200,
    height: 800,
    allowFullscreen: true,
  }
  isDialogVisible.value = true
}

// 👉 Open edit dialog
const openEditDialog = (app: any) => {
  currentApp.value = app
  appForm.value = {
    name: app.name,
    description: app.description || '',
    url: app.url,
    icon: app.icon || '',
    isActive: app.isActive,
    width: app.width || 1200,
    height: app.height || 800,
    allowFullscreen: app.allowFullscreen,
  }
  isEditDialogVisible.value = true
}

// 👉 Close dialogs
const closeAddDialog = () => {
  isDialogVisible.value = false
  appForm.value = {
    name: '',
    description: '',
    url: '',
    icon: '',
    isActive: true,
    width: 1200,
    height: 800,
    allowFullscreen: true,
  }
}

const closeEditDialog = () => {
  isEditDialogVisible.value = false
  currentApp.value = null
  appForm.value = {
    name: '',
    description: '',
    url: '',
    icon: '',
    isActive: true,
    width: 1200,
    height: 800,
    allowFullscreen: true,
  }
}

// 👉 Save app
const saveApp = async () => {
  try {
    saving.value = true

    if (currentApp.value) {
      // Update existing app
      await $api(`/external-apps/${currentApp.value.id}`, {
        method: 'PATCH',
        body: appForm.value,
      })
      showToast({
        type: 'success',
        message: t('externalApps.appUpdatedSuccessfully'),
      })
    }
    else {
      // Create new app
      await $api('/external-apps', {
        method: 'POST',
        body: appForm.value,
      })
      showToast({
        type: 'success',
        message: t('externalApps.appCreatedSuccessfully'),
      })
    }

    await loadExternalApps()
    closeAddDialog()
    closeEditDialog()
  }
  catch (error) {
    console.error('Failed to save external app:', error)
    showToast({
      type: 'error',
      message: t('externalApps.error'),
    })
  }
  finally {
    saving.value = false
  }
}

// 👉 Delete app function removed - only active/inactive toggle needed

// 👉 Toggle active status
const toggleActive = async (app: any) => {
  try {
    await $api(`/external-apps/${app.id}/toggle-active`, {
      method: 'PATCH',
    })
    showToast({
      type: 'success',
      message: t('externalApps.appStatusUpdatedSuccessfully'),
    })
    await loadExternalApps()
  }
  catch (error) {
    console.error('Failed to toggle app status:', error)
    showToast({
      type: 'error',
      message: t('externalApps.error'),
    })
  }
}

// 👉 Load data on mount
onMounted(() => {
  loadExternalApps()
})

// 👉 Watch for search and filter changes
watch([searchQuery, selectedStatus, page, itemsPerPage, sortBy, orderBy], () => {
  loadExternalApps()
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('admin.registerExternalApp') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('admin.externalAppSubtitle') }}
      </p>
    </div>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>Filters</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select Status -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedStatus"
              :placeholder="t('common.selectStatus')"
              :items="status"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: t('common.all') },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 15.625rem;">
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('externalApps.searchExternalApp')"
            />
          </div>

          <!-- 👉 Add external app button -->
          <VBtn
            prepend-icon="tabler-plus"
            @click="openAddDialog"
          >
            {{ t('common.add') }} {{ t('admin.registerExternalApp') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="externalApps"
        :items-length="totalItems"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- 👉 Name -->
        <template #item.name="{ item }">
          <div class="d-flex align-center gap-x-4">
            <VAvatar
              :image="item.icon"
              color="primary"
              variant="tonal"
              size="34"
            >
              <VIcon icon="tabler-apps" />
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.name || '' }}
              </h6>
              <div class="text-sm">
                {{ item.url || '' }}
              </div>
            </div>
          </div>
        </template>

        <!-- 👉 Description -->
        <template #item.description="{ item }">
          <span class="text-sm">
            {{ item.description || t('common.noDescription') }}
          </span>
        </template>

        <!-- 👉 Status -->
        <template #item.isActive="{ item }">
          <VChip
            :color="item.isActive ? 'success' : 'error'"
            size="small"
            label
            class="text-capitalize"
          >
            {{ item.isActive ? t('common.active') : t('common.inactive') }}
          </VChip>
        </template>

        <!-- 👉 Dimensions -->
        <template #item.dimensions="{ item }">
          <span class="text-sm">
            {{ item.width || 0 }}x{{ item.height || 0 }}
          </span>
        </template>

        <!-- 👉 Created At -->
        <template #item.createdAt="{ item }">
          <span class="text-sm">
            {{ formatDate24h(item.createdAt) }}
          </span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn @click="openEditDialog(item)">
            <VIcon icon="tabler-pencil" />
          </IconBtn>

          <IconBtn @click="toggleActive(item)">
            <VIcon :icon="item.isActive ? 'tabler-eye-off' : 'tabler-eye'" />
          </IconBtn>
        </template>

        <!-- pagination -->
        <template #bottom>
          <TablePagination
            v-model:page="page"
            :items-per-page="itemsPerPage"
            :total-items="totalItems"
          />
        </template>
      </VDataTableServer>
    <!-- SECTION -->
    </VCard>

    <!-- 👉 Add Dialog -->
    <VDialog
      v-model="isDialogVisible"
      max-width="600"
    >
      <VCard>
        <VCardTitle>
          {{ t('common.add') }} {{ t('admin.registerExternalApp') }}
        </VCardTitle>

        <VCardText>
          <VForm @submit.prevent="saveApp">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="appForm.name"
                  :label="t('common.name')"
                  :placeholder="t('common.enterName')"
                  required
                />
              </VCol>

              <VCol cols="12">
                <AppTextarea
                  v-model="appForm.description"
                  :label="t('common.description')"
                  :placeholder="t('common.enterDescription')"
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="appForm.url"
                  :label="t('common.url')"
                  :placeholder="t('common.enterUrl')"
                  type="url"
                  required
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="appForm.icon"
                  :label="t('common.icon')"
                  :placeholder="t('common.enterIconUrl')"
                  type="url"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model.number="appForm.width"
                  :label="t('common.width')"
                  type="number"
                  min="400"
                  max="2000"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model.number="appForm.height"
                  :label="t('common.height')"
                  type="number"
                  min="300"
                  max="1500"
                />
              </VCol>

              <VCol cols="12">
                <VCheckbox
                  v-model="appForm.allowFullscreen"
                  :label="t('common.allowFullscreen')"
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="closeAddDialog"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveApp"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 👉 Edit Dialog -->
    <VDialog
      v-model="isEditDialogVisible"
      max-width="600"
    >
      <VCard>
        <VCardTitle>
          {{ t('common.edit') }} {{ t('admin.registerExternalApp') }}
        </VCardTitle>

        <VCardText>
          <VForm @submit.prevent="saveApp">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="appForm.name"
                  :label="t('common.name')"
                  :placeholder="t('common.enterName')"
                  required
                />
              </VCol>

              <VCol cols="12">
                <AppTextarea
                  v-model="appForm.description"
                  :label="t('common.description')"
                  :placeholder="t('common.enterDescription')"
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="appForm.url"
                  :label="t('common.url')"
                  :placeholder="t('common.enterUrl')"
                  type="url"
                  required
                />
              </VCol>

              <VCol cols="12">
                <AppTextField
                  v-model="appForm.icon"
                  :label="t('common.icon')"
                  :placeholder="t('common.enterIconUrl')"
                  type="url"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model.number="appForm.width"
                  :label="t('common.width')"
                  type="number"
                  min="400"
                  max="2000"
                />
              </VCol>

              <VCol cols="6">
                <AppTextField
                  v-model.number="appForm.height"
                  :label="t('common.height')"
                  type="number"
                  min="300"
                  max="1500"
                />
              </VCol>

              <VCol cols="12">
                <VCheckbox
                  v-model="appForm.allowFullscreen"
                  :label="t('common.allowFullscreen')"
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="closeEditDialog"
          >
            {{ t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveApp"
          >
            {{ t('common.save') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
